import type { PixelConfig } from '@/config';
import type { StorageService } from '@/services/storage';

import type { EnsureSessionResult, SessionData, SessionService } from './types';
import { generateSessionId } from './session-id';

const DEFAULT_SESSION_KEY = 'session';

export class SessionServiceImpl implements SessionService {
  private readonly storage: StorageService;

  private readonly storageKey: string;

  constructor(storage: StorageService, config: PixelConfig) {
    this.storage = storage;
    this.storageKey = config.storage.keys?.session ?? DEFAULT_SESSION_KEY;
  }

  getSessionId(): string | null {
    return this.getSessionData()?.sessionId ?? null;
  }

  getSessionData(): SessionData | null {
    const raw = this.storage.get<SessionData | string>(this.storageKey);
    if (!raw) return null;

    if (typeof raw === 'string') {
      const now = Date.now();
      return {
        sessionId: raw,
        lastActivityAt: now,
        createdAt: now,
      };
    }

    return raw;
  }

  getLastActivityAt(): number | null {
    return this.getSessionData()?.lastActivityAt ?? null;
  }

  isExpired(ttlMs: number): boolean {
    const data = this.getSessionData();
    if (!data) return true;

    return Date.now() - data.lastActivityAt > ttlMs;
  }

  ensureSession(ttlMs: number): EnsureSessionResult {
    const data = this.getSessionData();
    const now = Date.now();

    if (!data) {
      const sessionId = generateSessionId();
      this.writeSessionData({
        sessionId,
        lastActivityAt: now,
        createdAt: now,
      });

      return {
        sessionId,
        expired: false,
        created: true,
      };
    }

    if (now - data.lastActivityAt > ttlMs) {
      const previousSessionId = data.sessionId;
      const sessionId = generateSessionId();

      this.writeSessionData({
        sessionId,
        lastActivityAt: now,
        createdAt: now,
      });

      return {
        sessionId,
        expired: true,
        previousSessionId,
        created: false,
      };
    }

    return {
      sessionId: data.sessionId,
      expired: false,
      created: false,
    };
  }

  ensureSessionId(): string {
    return this.ensureSession(Number.MAX_SAFE_INTEGER).sessionId;
  }

  touchActivity(): void {
    const data = this.getSessionData();
    if (!data) return;

    this.writeSessionData({
      ...data,
      lastActivityAt: Date.now(),
    });
  }

  renew(): string {
    const now = Date.now();
    const sessionId = generateSessionId();

    this.writeSessionData({
      sessionId,
      lastActivityAt: now,
      createdAt: now,
    });

    return sessionId;
  }

  clear(): void {
    this.storage.remove(this.storageKey);
  }

  private writeSessionData(data: SessionData): void {
    this.storage.set(this.storageKey, data);
  }
}
